import clsx from 'clsx';
import UploadCloudIcon from 'public/icons/upload-cloud.svg';
import React, { useCallback, useState } from 'react';
import { DropzoneInputProps, useDropzone } from 'react-dropzone';
import { Controller, FieldError } from 'react-hook-form';

import { Button } from '../Button/Button';
import { CircleLoader } from '../CircleLoader/CircleLoader';
import { FormError } from '../FormElements/FormElements';
import { Icon } from '../Icon/Icon';

import { DatasetFileCard } from './DatasetFileCard';
import styles from './FileUpload.module.scss';

interface FileAcceptType {
  [key: string]: string[];
}

interface FileExcludeType {
  extensions?: string[];
  mimeTypes?: string[];
}

export const parseFileName = (fullName: string): string => {
  const parsedName = fullName?.split('?')[0]?.split('/').pop() || '';

  const dotIdx = parsedName.lastIndexOf('.');
  if (dotIdx === -1) return parsedName;

  const ext = parsedName.slice(dotIdx); // includes the dot
  const base = parsedName.slice(0, dotIdx);

  const hash = base.slice(-8);

  if (hash[0] === '_') {
    return parseFileName(base.slice(0, -8) + ext);
  }

  return parsedName;
};

export interface FileUploadProps {
  name: string;
  error?: FieldError;
  file?: any;
  files?: any[];
  fileTypeDescription: string;
  control?: any;
  rules?: Object;
  acceptTypes?: FileAcceptType;
  excludeTypes?: FileExcludeType;
  onFileChanged?: (file?: any) => void;
  onFilesChanged?: (files?: any[]) => void;
  onFileDownload?: (url: string | File | any, name: string, acceptTypes?: FileAcceptType) => Promise<void> | void;
  multiple?: boolean;
  className?: string;
  fileName?: string;
  inputTestId?: string;
  datasetPreview?: boolean;
}

interface UploadStateProps {
  uploading: boolean;
  error?: FieldError;
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T;
  open: () => void;
  fileTypeDescription: string;
  inputTestId?: string;
}

const UploadedState: React.FC<UploadStateProps> = ({
  uploading,
  getInputProps,
  open,
  fileTypeDescription,
  inputTestId,
}) =>
  uploading ? (
    <div className={styles.loader}>
      <CircleLoader />
    </div>
  ) : (
    <>
      <input
        className={styles.input}
        {...getInputProps({ 'data-testid': inputTestId } as DropzoneInputProps)}
      />
      <div className={styles.circle}>
        <div className={clsx(styles.circle, styles.small)}>
          <UploadCloudIcon />
        </div>
      </div>
      <p className={styles.controlWrapper}>
        <Button
          view="tertiary"
          type="button"
          className={styles.button}
          onClick={open}
        >
          Click to upload
        </Button>
        <span>or drag and drop</span>
      </p>
      <p className={styles.typeDesc}>{fileTypeDescription}</p>
    </>
  );

export const FileUpload: React.FC<FileUploadProps> = ({
  acceptTypes,
  excludeTypes,
  control,
  name,
  rules,
  file,
  files = [],
  fileTypeDescription,
  onFileChanged,
  onFilesChanged,
  onFileDownload,
  className,
  multiple = false,
  fileName,
  inputTestId,
  datasetPreview = false,
}) => {
  const uploading = false; // no immediate upload for now
  const [typeError, setTypeError] = useState<string | null>(null);

  const onDrop = useCallback<(dropFiles: File[]) => void>(
    async (newFiles: File[]) => {
      if (newFiles) {
        // First filter out empty files
        const nonEmptyFiles = newFiles.filter(newFile => newFile.size > 0);
        if (nonEmptyFiles.length === 0) {
          setTypeError('Selected file(s) are empty. Please select files in the correct format.');
          return;
        }

        // Apply exclusion logic if excludeTypes is provided
        let validFiles = nonEmptyFiles;
        if (excludeTypes) {
          validFiles = nonEmptyFiles.filter(fileItem => {
            const fileNameLowercase = fileItem.name.toLowerCase();
            const fileType = fileItem.type.toLowerCase();

            // Check if file extension is excluded
            const hasExcludedExtension =
              excludeTypes.extensions?.some(ext => fileNameLowercase.endsWith(ext.toLowerCase())) || false;

            // Check if MIME type is excluded
            const hasExcludedMimeType =
              excludeTypes.mimeTypes?.some(mimeType => fileType.includes(mimeType.toLowerCase())) || false;

            return !hasExcludedExtension && !hasExcludedMimeType;
          });
        }

        if (validFiles.length === 0) {
          setTypeError('Selected file(s) are not allowed. Please select files of the correct type.');
          return;
        }

        const newUploadedFiles = validFiles.map(f => ({
          name: f.name,
          file: f,
        }));

        if (multiple && onFilesChanged) {
          onFilesChanged([...files, ...newUploadedFiles]);
        } else if (onFileChanged) {
          onFileChanged(newUploadedFiles[0]);
        }
        setTypeError(null);
      } else {
        // unaccepted file type is dropped
        setTypeError('Please upload the file of the correct type');
      }
    },
    [files, multiple, onFilesChanged, onFileChanged, excludeTypes],
  );

  const onRemove = () => {
    if (multiple && onFilesChanged) {
      onFilesChanged(undefined);
    } else if (onFileChanged) {
      onFileChanged(undefined);
    }
  };

  // Generate accept pattern for visual feedback when using excludeTypes
  const getAcceptPattern = () => {
    if (acceptTypes) {
      return acceptTypes;
    }

    if (excludeTypes) {
      // Create a comprehensive accept pattern that includes most common file types
      // but excludes the specific types we want to reject
      const commonTypes = {
        'text/*': [],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-powerpoint': ['.ppt'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
        'text/plain': ['.txt'],
        'text/csv': ['.csv'],
        'text/html': ['.html'],
        'text/markdown': ['.md'],
        'application/json': ['.json'],
        'application/xml': ['.xml'],
        'application/zip': ['.zip'],
        'application/x-rar-compressed': ['.rar'],
        'application/x-7z-compressed': ['.7z'],
        'image/*': [],
        'video/*': [],
        'audio/*': [],
      };

      // Filter out excluded extensions from the accept pattern
      const filteredTypes = { ...commonTypes };

      if (excludeTypes.extensions) {
        // Remove MIME types that correspond to excluded extensions
        Object.keys(filteredTypes).forEach(mimeType => {
          const extensions = filteredTypes[mimeType];
          if (extensions && extensions.length > 0) {
            filteredTypes[mimeType] = extensions.filter(
              ext => !excludeTypes.extensions?.some(excludedExt => ext.toLowerCase() === excludedExt.toLowerCase()),
            );
            // Remove MIME type if no extensions remain
            if (filteredTypes[mimeType].length === 0) {
              delete filteredTypes[mimeType];
            }
          }
        });
      }

      return filteredTypes;
    }

    return undefined;
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled: uploading,
    noClick: true,
    accept: getAcceptPattern(),
    multiple,
  });

  let previewFile: File | undefined;
  if (file?.file instanceof File) previewFile = file.file;
  else if (file instanceof File) previewFile = file;

  const showDatasetCard = datasetPreview && !multiple && !!previewFile;

  const renderBody = () => {
    if (showDatasetCard && previewFile) {
      return (
        <DatasetFileCard
          file={previewFile}
          fileName={parseFileName(fileName || file.name || file)}
          onDownload={() =>
            onFileDownload && onFileDownload(previewFile, parseFileName(fileName || file.name || file), acceptTypes)
          }
          onRemove={onRemove}
        />
      );
    }

    if (!multiple && file) {
      return (
        <div className={styles.uploadedWrapper}>
          <div
            onClick={() =>
              onFileDownload &&
              onFileDownload(
                file?.file instanceof File ? file.file : file.name,
                parseFileName(fileName || file.name || file),
                acceptTypes,
              )
            }
            className={styles.fileName}
          >
            <span>{parseFileName(fileName || file.name || file)}</span>
            <Icon name="download" />
          </div>
          <Button
            type="button"
            className={styles.button}
            view="tertiary"
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      );
    }

    return (
      <UploadedState
        uploading={uploading}
        getInputProps={getInputProps}
        open={open}
        fileTypeDescription={fileTypeDescription}
        inputTestId={inputTestId}
      />
    );
  };

  const renderContent = (error?: FieldError) => (
    <div
      {...getRootProps()}
      className={clsx(className, {
        [styles.dropzone]: !showDatasetCard,
        [styles.card]: showDatasetCard,
        [styles.error]: typeError || error,
        [styles.isDragActive]: isDragActive && !showDatasetCard,
      })}
    >
      {renderBody()}
      {!typeError && error && (
        <FormError
          className={styles.errorLabel}
          error={error.message}
        />
      )}
      {typeError && (
        <FormError
          className={styles.errorLabel}
          error={typeError}
        />
      )}
    </div>
  );

  return control ? (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState: { error }, field: { ref } }) => (
        <div
          className={styles.fileUploadWrapper}
          ref={ref}
        >
          {renderContent(error)}
        </div>
      )}
    />
  ) : (
    renderContent()
  );
};
